import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * `containerClassName` is a local addition to the generated component, and a
 * sticky header is why it exists.
 *
 * This wrapper is the nearest scrolling ancestor of anything in the table,
 * because `overflow-x-auto` makes the OTHER axis compute to `auto` too — CSS
 * resolves `visible` to `auto` as soon as one axis is not visible. So a
 * `position: sticky` thead sticks relative to THIS element, whatever a parent
 * does. When the scroll box was a separate div outside it, the wrapper was
 * full content height, never scrolled, and the sticky header therefore had
 * nothing to stick against: it scrolled away with the rows.
 *
 * Letting the caller put the height cap and the overflow HERE collapses the
 * two boxes into one, so the element the rows scroll in is the element the
 * header sticks to.
 */
function Table({
  className,
  containerClassName,
  containerStyle,
  containerRef,
  ...props
}: React.ComponentProps<"table"> & {
  containerClassName?: string
  /** Goes on the wrapper, not the table: a max-height on a <table> makes no
   *  scroll box, so the header would have nothing to stick against. */
  containerStyle?: React.CSSProperties
  /** So a caller can measure the box it actually scrolls in. */
  containerRef?: React.Ref<HTMLDivElement>
}) {
  return (
    <div
      ref={containerRef}
      data-slot="table-container"
      className={cn("relative w-full overflow-x-auto", containerClassName)}
      style={containerStyle}
    >
      <table
        data-slot="table"
        className={cn("w-full caption-bottom text-sm", className)}
        {...props}
      />
    </div>
  )
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn("[&_tr]:border-b", className)}
      {...props}
    />
  )
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  )
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
        className
      )}
      {...props}
    />
  )
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "border-b transition-colors hover:bg-muted/50 has-aria-expanded:bg-muted/50 data-[state=selected]:bg-muted",
        className
      )}
      {...props}
    />
  )
}

function TableHead({ className, ...props }: React.ComponentProps<"th">) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        "h-10 px-2 text-left align-middle font-medium whitespace-nowrap text-foreground [&:has([role=checkbox])]:pr-0",
        className
      )}
      {...props}
    />
  )
}

function TableCell({ className, ...props }: React.ComponentProps<"td">) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        "p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0",
        className
      )}
      {...props}
    />
  )
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("mt-4 text-sm text-muted-foreground", className)}
      {...props}
    />
  )
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
